import React, { useState, useRef, useEffect } from 'react';
import Transition from '../utils/Transition';

function DropdownAI({options, style, onChange, selectedOption}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selected, setSelected] = useState(selectedOption);
  const [isFlickering, setIsFlickering] = useState(false);
  const [previousOption, setPreviousOption] = useState(selectedOption);
  
  const trigger = useRef(null);
  const dropdown = useRef(null);

  // Update selected when selectedOption prop changes
  useEffect(() => {
    if (selectedOption !== previousOption) {
      setPreviousOption(selectedOption);
      setSelected(selectedOption);
      setIsFlickering(true);
      
      setTimeout(() => {
        setIsFlickering(false);
      }, 1500); // Single pulse with 1.5s duration
    }
  }, [selectedOption]);

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (!dropdownOpen || dropdown.current.contains(target) || trigger.current.contains(target)) return;
      setDropdownOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  const getFlickerClass = () => {
    if (!isFlickering) return '';
    
    switch (selected) {
      case 1: return 'flicker-red';
      case 2: return 'flicker-yellow';
      case 3: return 'flicker-green';
      default: return '';
    }
  };

  // Find the currently selected option
  const selectedOptionData = options.find(option => option.id === selected) || options[0];

  return (
    <div className={`relative inline-flex ${style}`}>
      <button
        ref={trigger}
        className={`btn justify-between min-w-44 dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-500 hover:text-slate-600 dark:text-slate-300 dark:hover:text-slate-200 ${style} ${getFlickerClass()}`}
        aria-label="Select option"
        aria-haspopup="true"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-expanded={dropdownOpen}
      >
        <span className="flex items-center">
          <span>{selectedOptionData?.label}</span>
        </span>
        <svg className="shrink-0 ml-1 fill-current text-slate-400" width="11" height="7" viewBox="0 0 11 7">
          <path d="M5.4 6.8L0 1.4 1.4 0l4 4 4-4 1.4 1.4z" />
        </svg>
      </button>

      <style jsx>{`
        @keyframes flickerRed {
          0% { background-color: transparent; }
          30% { background-color: rgb(239 68 68 / 0.3); }
          100% { background-color: transparent; }
        }

        @keyframes flickerYellow {
          0% { background-color: transparent; }
          30% { background-color: rgb(234 179 8 / 0.3); }
          100% { background-color: transparent; }
        }

        @keyframes flickerGreen {
          0% { background-color: transparent; }
          30% { background-color: rgb(34 197 94 / 0.3); }
          100% { background-color: transparent; }
        }

        .flicker-red {
          animation: flickerRed 1.5s ease-in-out;
        }

        .flicker-yellow {
          animation: flickerYellow 1.5s ease-in-out;
        }

        .flicker-green {
          animation: flickerGreen 1.5s ease-in-out;
        }
      `}</style>

      <Transition
        show={dropdownOpen}
        tag="div"
        className="z-10 absolute top-full left-0 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 py-1.5 rounded shadow-lg overflow-hidden mt-1"
        enter="transition ease-out duration-100 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-100"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
      >
        <div
          ref={dropdown}
          className="font-medium text-sm text-slate-600 dark:text-slate-300"
          onFocus={() => setDropdownOpen(true)}
          onBlur={() => setDropdownOpen(false)}
        >
          {options.map(option => (
            <button
              key={option.id}
              tabIndex="0"
              className={`flex items-center w-full hover:bg-slate-50 hover:dark:bg-slate-700/20 py-1 px-3 cursor-pointer text-xs ${option.id === selected && 'text-indigo-500'}`}
              onClick={() => {
                setSelected(option.id);
                setDropdownOpen(false);
                onChange(option.id);
              }}
            >
              <svg className={`shrink-0 mr-2 fill-current text-indigo-500 ${option.id !== selected && 'invisible'}`} width="12" height="9" viewBox="0 0 12 9">
                <path d="M10.28.28L3.989 6.575 1.695 4.28A1 1 0 00.28 5.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28.28z" />
              </svg>
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      </Transition>
    </div>
  );
}

export default DropdownAI;